/**
 * ActionButtonConfigurator — T12.D.7 + T12.D.cm.
 *
 * Lista i edycja przycisków akcji per moduł.
 * Używany w zakładce "Przyciski akcji" w ModuleSettingsPage.
 *
 * Obsługiwane typy akcji:
 *  - update_field  — zmień wartość pola rekordu
 *  - webhook       — wywołaj zewnętrzny URL HTTP
 *  - navigate      — przejdź do URL (z tokenami {slug})
 *  - create_record — utwórz rekord w innym module z pre-fillem (T12.D.cm)
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown,
  ChevronRight,
  Edit3,
  ExternalLink,
  FilePlus,
  Globe,
  Plus,
  Trash2,
  Zap,
} from 'lucide-react';

import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Dropdown } from '@/components/Dropdown';
import type { ModuleField } from '@/components/fields/FieldRegistry';
import { Input } from '@/components/Input';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

import {
  CreateRecordActionConfig,
  type CreateRecordConfig,
  type FieldMapping,
} from './CreateRecordActionConfig';

// ── Typy ─────────────────────────────────────────────────────────────────────

export type ActionType = 'update_field' | 'webhook' | 'navigate' | 'create_record';
export type ActionVariant = 'primary' | 'secondary' | 'danger' | 'warning';
export type ActionPosition = 'record_actions' | 'toolbar';

export interface ModuleAction {
  id: string;
  moduleId: string;
  name: string;
  icon: string;
  variant: ActionVariant;
  position: ActionPosition;
  actionType: ActionType;
  actionConfig: Record<string, unknown>;
  showConditions: Array<{ field: string; operator: string; value: unknown }>;
  requireConfirmation: boolean;
  confirmationMessage: string | null;
  sortOrder: number;
}

interface ActionFormState {
  name: string;
  icon: string;
  variant: ActionVariant;
  position: ActionPosition;
  actionType: ActionType;
  // update_field config
  updateField: string;
  updateValue: string;
  // webhook config
  webhookUrl: string;
  webhookMethod: 'GET' | 'POST';
  webhookIncludeRecord: boolean;
  // navigate config
  navigateUrl: string;
  navigateNewTab: boolean;
  // create_record config (T12.D.cm)
  createTargetModuleId: string;
  createTargetCategoryId: string | null;
  createFieldMappings: FieldMapping[];
  // common
  requireConfirmation: boolean;
  confirmationMessage: string;
}

function defaultForm(): ActionFormState {
  return {
    name: '',
    icon: 'Zap',
    variant: 'secondary',
    position: 'record_actions',
    actionType: 'update_field',
    updateField: '',
    updateValue: '',
    webhookUrl: '',
    webhookMethod: 'POST',
    webhookIncludeRecord: true,
    navigateUrl: '',
    navigateNewTab: false,
    createTargetModuleId: '',
    createTargetCategoryId: null,
    createFieldMappings: [],
    requireConfirmation: false,
    confirmationMessage: '',
  };
}

function formToPayload(form: ActionFormState) {
  let actionConfig: Record<string, unknown> = {};
  switch (form.actionType) {
    case 'update_field':
      actionConfig = {
        updates: form.updateField ? [{ field: form.updateField, value: form.updateValue }] : [],
      };
      break;
    case 'webhook':
      actionConfig = {
        url: form.webhookUrl,
        method: form.webhookMethod,
        includeRecord: form.webhookIncludeRecord,
      };
      break;
    case 'navigate':
      actionConfig = {
        urlTemplate: form.navigateUrl,
        openInNewTab: form.navigateNewTab,
      };
      break;
    case 'create_record':
      actionConfig = {
        targetModuleId: form.createTargetModuleId,
        targetCategoryId: form.createTargetCategoryId,
        fieldMappings: form.createFieldMappings,
      };
      break;
  }
  return {
    name: form.name.trim(),
    icon: form.icon || 'Zap',
    variant: form.variant,
    position: form.position,
    actionType: form.actionType,
    actionConfig,
    requireConfirmation: form.requireConfirmation,
    confirmationMessage: form.confirmationMessage.trim() || null,
  };
}

function actionToForm(action: ModuleAction): ActionFormState {
  const cfg = action.actionConfig;
  const form = defaultForm();
  form.name = action.name;
  form.icon = action.icon;
  form.variant = action.variant;
  form.position = action.position;
  form.actionType = action.actionType;
  form.requireConfirmation = action.requireConfirmation;
  form.confirmationMessage = action.confirmationMessage ?? '';

  switch (action.actionType) {
    case 'update_field': {
      const updates = (cfg.updates as Array<{ field: string; value: unknown }> | undefined) ?? [];
      form.updateField = String(updates[0]?.field ?? '');
      form.updateValue = updates[0]?.value != null ? String(updates[0].value) : '';
      break;
    }
    case 'webhook':
      form.webhookUrl = String(cfg.url ?? '');
      form.webhookMethod = (cfg.method as 'GET' | 'POST' | undefined) ?? 'POST';
      form.webhookIncludeRecord = cfg.includeRecord !== false;
      break;
    case 'navigate':
      form.navigateUrl = String(cfg.urlTemplate ?? '');
      form.navigateNewTab = cfg.openInNewTab === true;
      break;
    case 'create_record':
      form.createTargetModuleId = String(cfg.targetModuleId ?? '');
      form.createTargetCategoryId =
        cfg.targetCategoryId != null ? String(cfg.targetCategoryId) : null;
      form.createFieldMappings = Array.isArray(cfg.fieldMappings)
        ? (cfg.fieldMappings as FieldMapping[])
        : [];
      break;
  }
  return form;
}

const ACTION_TYPE_ICONS: Record<ActionType, typeof Zap> = {
  update_field: Edit3,
  webhook: Globe,
  navigate: ExternalLink,
  create_record: FilePlus,
};

// ── Component ────────────────────────────────────────────────────────────────

interface ActionButtonConfiguratorProps {
  moduleId: string;
  fields: ModuleField[];
}

export function ActionButtonConfigurator({ moduleId, fields }: ActionButtonConfiguratorProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newForm, setNewForm] = useState<ActionFormState>(defaultForm());
  const [editForms, setEditForms] = useState<Record<string, ActionFormState>>({});
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);

  const actionsQuery = useQuery<{ actions: ModuleAction[] }>({
    queryKey: ['module-actions', moduleId],
    queryFn: async () => {
      const res = await api.get(`/modules/${moduleId}/actions`);
      return res.data;
    },
    enabled: !!moduleId,
  });

  const createMutation = useMutation({
    mutationFn: async (payload: ReturnType<typeof formToPayload>) => {
      const res = await api.post(`/modules/${moduleId}/actions`, payload);
      return res.data;
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.modules.actionButtons.toasts.created') });
      qc.invalidateQueries({ queryKey: ['module-actions', moduleId] });
      setShowNewForm(false);
      setNewForm(defaultForm());
    },
    onError: () => {
      toast.show({ variant: 'error', title: t('tenant.modules.actionButtons.toasts.createError') });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      actionId,
      payload,
    }: {
      actionId: string;
      payload: ReturnType<typeof formToPayload>;
    }) => {
      const res = await api.put(`/modules/${moduleId}/actions/${actionId}`, payload);
      return res.data;
    },
    onSuccess: (_data, { actionId }) => {
      toast.show({ variant: 'success', title: t('tenant.modules.actionButtons.toasts.saved') });
      qc.invalidateQueries({ queryKey: ['module-actions', moduleId] });
      setExpandedId(null);
      setEditForms((prev) => {
        const next = { ...prev };
        delete next[actionId];
        return next;
      });
    },
    onError: () => {
      toast.show({ variant: 'error', title: t('tenant.modules.actionButtons.toasts.saveError') });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (actionId: string) => {
      await api.delete(`/modules/${moduleId}/actions/${actionId}`);
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.modules.actionButtons.toasts.deleted') });
      qc.invalidateQueries({ queryKey: ['module-actions', moduleId] });
    },
    onError: () => {
      toast.show({ variant: 'error', title: t('tenant.modules.actionButtons.toasts.deleteError') });
    },
  });

  const actions = actionsQuery.data?.actions ?? [];

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-text-primary">
            {t('tenant.modules.actionButtons.title')}
          </h3>
          <p className="mt-0.5 text-xs text-text-muted">
            {t('tenant.modules.actionButtons.description')}
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={() => {
            setShowNewForm(true);
            setExpandedId(null);
          }}
          data-testid="action-add-btn"
        >
          <Plus className="h-3.5 w-3.5" />
          {t('tenant.modules.actionButtons.add')}
        </Button>
      </div>

      {/* Formularz nowej akcji */}
      {showNewForm && (
        <ActionForm
          form={newForm}
          fields={fields}
          moduleId={moduleId}
          onChange={setNewForm}
          onSave={() => createMutation.mutate(formToPayload(newForm))}
          onCancel={() => {
            setShowNewForm(false);
            setNewForm(defaultForm());
          }}
          saving={createMutation.isPending}
          isNew
        />
      )}

      {/* Lista akcji */}
      {actions.length === 0 && !showNewForm && (
        <p className="py-8 text-center text-sm text-text-muted" data-testid="actions-empty">
          {t('tenant.modules.actionButtons.empty')}
        </p>
      )}

      <div className="space-y-2">
        {actions.map((action) => {
          const isExpanded = expandedId === action.id;
          const TypeIcon = ACTION_TYPE_ICONS[action.actionType] ?? Zap;
          const editForm = editForms[action.id] ?? actionToForm(action);

          return (
            <div
              key={action.id}
              className="rounded-lg border border-border-primary bg-bg-primary"
              data-testid={`action-card-${action.id}`}
            >
              {/* Nagłówek akcji */}
              <div className="flex items-center gap-2 px-3 py-2">
                <button
                  type="button"
                  onClick={() => {
                    if (!isExpanded) {
                      setEditForms((prev) => ({ ...prev, [action.id]: actionToForm(action) }));
                    }
                    setExpandedId(isExpanded ? null : action.id);
                  }}
                  className="flex flex-1 items-center gap-2 text-left"
                >
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 shrink-0 text-text-muted" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0 text-text-muted" />
                  )}
                  <TypeIcon className="h-4 w-4 shrink-0 text-accent-500" />
                  <span className="flex-1 truncate text-sm font-medium text-text-primary">
                    {action.name}
                  </span>
                </button>

                <span
                  className={`shrink-0 rounded px-1.5 py-0.5 text-xs font-medium ${
                    action.variant === 'primary'
                      ? 'bg-accent-100 text-accent-700'
                      : action.variant === 'danger'
                        ? 'bg-danger-100 text-danger-700'
                        : action.variant === 'warning'
                          ? 'bg-warning-100 text-warning-700'
                          : 'bg-bg-secondary text-text-secondary'
                  }`}
                >
                  {t(`tenant.modules.actionButtons.variants.${action.variant}`)}
                </span>

                <span className="shrink-0 rounded bg-bg-secondary px-1.5 py-0.5 text-xs text-text-muted">
                  {t(`tenant.modules.actionButtons.types.${action.actionType}`)}
                </span>

                <button
                  type="button"
                  onClick={() => setPendingDelete({ id: action.id, name: action.name })}
                  className="shrink-0 rounded p-1 text-text-muted hover:text-danger-600 hover:bg-danger-50"
                  data-testid={`action-delete-${action.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>

              {/* Rozwinięty formularz edycji */}
              {isExpanded && (
                <div className="border-t border-border-primary px-3 pb-3 pt-2">
                  <ActionForm
                    form={editForm}
                    fields={fields}
                    moduleId={moduleId}
                    onChange={(f) => setEditForms((prev) => ({ ...prev, [action.id]: f }))}
                    onSave={() =>
                      updateMutation.mutate({
                        actionId: action.id,
                        payload: formToPayload(editForm),
                      })
                    }
                    onCancel={() => {
                      setExpandedId(null);
                      setEditForms((prev) => {
                        const n = { ...prev };
                        delete n[action.id];
                        return n;
                      });
                    }}
                    saving={updateMutation.isPending}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('common.delete')}
        message={t('tenant.modules.actionButtons.deleteConfirm', {
          name: pendingDelete?.name ?? '',
        })}
        confirmLabel={t('common.delete')}
        onConfirm={() => {
          if (pendingDelete) deleteMutation.mutate(pendingDelete.id);
          setPendingDelete(null);
        }}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  );
}

// ── ActionForm ────────────────────────────────────────────────────────────────

interface ActionFormProps {
  form: ActionFormState;
  fields: ModuleField[];
  /** ID modułu źródła (do wykluczenia z listy modułów docelowych w create_record). */
  moduleId: string;
  onChange: (f: ActionFormState) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  isNew?: boolean;
}

function ActionForm({
  form,
  fields,
  moduleId,
  onChange,
  onSave,
  onCancel,
  saving,
  isNew,
}: ActionFormProps) {
  const { t } = useTranslation();
  const set = (patch: Partial<ActionFormState>) => onChange({ ...form, ...patch });
  const inputCls =
    'w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1.5 text-sm text-text-primary focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500';
  const labelCls = 'mb-1 block text-xs text-text-secondary';

  return (
    <div
      className="space-y-3 rounded-lg border border-border-primary bg-bg-secondary p-3"
      data-testid={isNew ? 'action-new-form' : 'action-edit-form'}
    >
      {/* Nazwa + Typ */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>{t('tenant.modules.actionButtons.fields.name')}</label>
          <Input
            type="text"
            value={form.name}
            onChange={(e) => set({ name: e.target.value })}
            placeholder={t('tenant.modules.actionButtons.fields.namePlaceholder')}
            className={inputCls}
            data-testid="action-form-name"
          />
        </div>
        <div>
          <label className={labelCls}>{t('tenant.modules.actionButtons.fields.actionType')}</label>
          <Dropdown
            value={form.actionType}
            onChange={(v) => set({ actionType: v as ActionType })}
            className={inputCls}
            data-testid="action-form-type"
            options={[
              {
                value: 'update_field',
                label: t('tenant.modules.actionButtons.types.update_field'),
              },
              { value: 'webhook', label: t('tenant.modules.actionButtons.types.webhook') },
              { value: 'navigate', label: t('tenant.modules.actionButtons.types.navigate') },
              {
                value: 'create_record',
                label: t('tenant.modules.actionButtons.types.create_record'),
              },
            ]}
          />
        </div>
      </div>

      {/* Variant + Position */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>{t('tenant.modules.actionButtons.fields.variant')}</label>
          <Dropdown
            value={form.variant}
            onChange={(v) => set({ variant: v as ActionVariant })}
            className={inputCls}
            options={[
              { value: 'primary', label: t('tenant.modules.actionButtons.variants.primary') },
              { value: 'secondary', label: t('tenant.modules.actionButtons.variants.secondary') },
              { value: 'danger', label: t('tenant.modules.actionButtons.variants.danger') },
              { value: 'warning', label: t('tenant.modules.actionButtons.variants.warning') },
            ]}
          />
        </div>
        <div>
          <label className={labelCls}>{t('tenant.modules.actionButtons.fields.position')}</label>
          <Dropdown
            value={form.position}
            onChange={(v) => set({ position: v as ActionPosition })}
            className={inputCls}
            options={[
              {
                value: 'record_actions',
                label: t('tenant.modules.actionButtons.positions.record_actions'),
              },
              { value: 'toolbar', label: t('tenant.modules.actionButtons.positions.toolbar') },
            ]}
          />
        </div>
      </div>

      {/* Konfiguracja wg typu */}
      {form.actionType === 'update_field' && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>
              {t('tenant.modules.actionButtons.updateField.field')}
            </label>
            <Dropdown
              value={form.updateField}
              onChange={(v) => set({ updateField: v })}
              className={inputCls}
              data-testid="action-form-update-field"
              options={[
                { value: '', label: t('tenant.modules.actionButtons.updateField.selectField') },
                ...fields.map((f) => ({ value: f.slug, label: f.name })),
              ]}
            />
          </div>
          <div>
            <label className={labelCls}>
              {t('tenant.modules.actionButtons.updateField.value')}
            </label>
            <Input
              type="text"
              value={form.updateValue}
              onChange={(e) => set({ updateValue: e.target.value })}
              placeholder={t('tenant.modules.actionButtons.updateField.valuePlaceholder')}
              className={inputCls}
              data-testid="action-form-update-value"
            />
          </div>
        </div>
      )}

      {form.actionType === 'webhook' && (
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <label className={labelCls}>{t('tenant.modules.actionButtons.webhook.url')}</label>
              <Input
                type="url"
                value={form.webhookUrl}
                onChange={(e) => set({ webhookUrl: e.target.value })}
                placeholder="https://example.com/webhook"
                className={inputCls}
                data-testid="action-form-webhook-url"
              />
            </div>
            <div>
              <label className={labelCls}>{t('tenant.modules.actionButtons.webhook.method')}</label>
              <Dropdown
                value={form.webhookMethod}
                onChange={(v) => set({ webhookMethod: v as 'GET' | 'POST' })}
                className={inputCls}
                options={[
                  { value: 'POST', label: 'POST' },
                  { value: 'GET', label: 'GET' },
                ]}
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={form.webhookIncludeRecord}
              onChange={(e) => set({ webhookIncludeRecord: e.target.checked })}
              className="h-3.5 w-3.5 rounded border-border-primary text-accent-600"
            />
            <span className="text-text-secondary">
              {t('tenant.modules.actionButtons.webhook.includeRecord')}
            </span>
          </label>
        </div>
      )}

      {form.actionType === 'navigate' && (
        <div className="space-y-2">
          <div>
            <label className={labelCls}>
              {t('tenant.modules.actionButtons.navigate.urlTemplate')}
            </label>
            <Input
              type="text"
              value={form.navigateUrl}
              onChange={(e) => set({ navigateUrl: e.target.value })}
              placeholder="/modules/my-module/{id}"
              className={inputCls}
              data-testid="action-form-navigate-url"
            />
            <p className="mt-0.5 text-xs text-text-muted">
              {t('tenant.modules.actionButtons.navigate.urlHint')}
            </p>
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={form.navigateNewTab}
              onChange={(e) => set({ navigateNewTab: e.target.checked })}
              className="h-3.5 w-3.5 rounded border-border-primary text-accent-600"
            />
            <span className="text-text-secondary">
              {t('tenant.modules.actionButtons.navigate.newTab')}
            </span>
          </label>
        </div>
      )}

      {form.actionType === 'create_record' && (
        <CreateRecordActionConfig
          sourceFields={fields}
          currentModuleId={moduleId}
          value={{
            targetModuleId: form.createTargetModuleId,
            targetCategoryId: form.createTargetCategoryId,
            fieldMappings: form.createFieldMappings,
          }}
          onChange={(v: CreateRecordConfig) =>
            set({
              createTargetModuleId: v.targetModuleId,
              createTargetCategoryId: v.targetCategoryId,
              createFieldMappings: v.fieldMappings,
            })
          }
        />
      )}

      {/* Potwierdzenie */}
      <div className="space-y-1.5">
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={form.requireConfirmation}
            onChange={(e) => set({ requireConfirmation: e.target.checked })}
            className="h-3.5 w-3.5 rounded border-border-primary text-accent-600"
          />
          <span className="text-text-secondary">
            {t('tenant.modules.actionButtons.fields.requireConfirmation')}
          </span>
        </label>
        {form.requireConfirmation && (
          <Input
            type="text"
            value={form.confirmationMessage}
            onChange={(e) => set({ confirmationMessage: e.target.value })}
            placeholder={t('tenant.modules.actionButtons.fields.confirmationMessagePlaceholder')}
            className={inputCls}
          />
        )}
      </div>

      {/* Przyciski */}
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="secondary" size="sm" onClick={onCancel} disabled={saving}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={onSave}
          loading={saving}
          disabled={saving || !form.name.trim()}
          data-testid="action-form-save"
        >
          {isNew ? t('common.create') : t('common.save')}
        </Button>
      </div>
    </div>
  );
}
